import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { getDamageType } from "@/utils/damageTypeData";
import { Shield, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import HeroSection from "@/components/layout/HeroSection";
import CTASection from "@/components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getOrganizationSchema, getBreadcrumbSchema } from "@/utils/seoSchemas";
import ransomwareHero from "@/assets/damage-types/ransomware-hero.jpg";
import datenverlustHero from "@/assets/damage-types/datenverlust-hero.jpg";
import phishingHero from "@/assets/damage-types/phishing-hero.jpg";
import patientendatenHero from "@/assets/damage-types/patientendaten-diebstahl-hero.jpg";
import betriebsunterbrechungHero from "@/assets/damage-types/betriebsunterbrechung-hero.jpg";
import dsgvoHero from "@/assets/damage-types/dsgvo-hero.jpg";

interface DamageTypePageProps {
  slug: string;
}

const DamageTypePage = ({ slug }: DamageTypePageProps) => {
  const damageType = getDamageType(slug);

  if (!damageType) {
    return <div>Schaden nicht gefunden</div>;
  }

  const damageTypeImages: Record<string, string> = {
    "ransomware-schutz": ransomwareHero,
    "datenverlust": datenverlustHero,
    "phishing-schutz": phishingHero,
    "patientendaten-diebstahl": patientendatenHero,
    "betriebsunterbrechung": betriebsunterbrechungHero,
    "dsgvo-bussgelder": dsgvoHero,
  };

  const heroImage = damageTypeImages[slug];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Home", url: "https://praxis-versichert.de" },
    { name: "Cyberversicherung", url: "https://praxis-versichert.de/cyberversicherung" },
    { name: damageType.name }
  ]);

  return (
    <>
      <Helmet>
        <title>{damageType.title}</title>
        <meta name="description" content={damageType.metaDescription} />
        <link rel="canonical" href={`https://praxis-versichert.de/cyberversicherung/${damageType.slug}`} />
        
        <meta property="og:title" content={damageType.title} />
        <meta property="og:description" content={damageType.metaDescription} />
        
        <script type="application/ld+json">
          {JSON.stringify(getOrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <Navigation />
      
      <main>
        {/* Hero */}
        {heroImage && (
          <HeroSection image={heroImage} alt={damageType.heroTitle} title={damageType.heroTitle} gradientPreset="none" imageOpacity={40} overlayColor="background" className="py-20 sm:py-24 lg:py-32 px-4">
            <div className="container mx-auto max-w-4xl text-center relative z-10">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-6">
                <Shield className="w-6 h-6 text-accent" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{damageType.heroTitle}</h1>
              <p className="text-lg text-foreground max-w-2xl mx-auto font-semibold mb-6">{damageType.heroSubtitle}</p>
            </div>
          </HeroSection>
        )}
        
        {!heroImage && (
          <section className="py-20 sm:py-24 lg:py-32 px-4 bg-background">
            <div className="container mx-auto max-w-4xl text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-6">
                <Shield className="w-6 h-6 text-accent" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{damageType.heroTitle}</h1>
              <p className="text-lg text-foreground max-w-2xl mx-auto font-semibold mb-6">{damageType.heroSubtitle}</p>
            </div>
          </section>
        )}

        {/* Stats */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {damageType.statistics.map((stat, i) => (
                <div key={i} className="text-center p-6 bg-card rounded-lg border border-border">
                  <div className="text-3xl font-bold text-accent mb-2">{stat.value}</div>
                  <div className="text-sm font-semibold mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Description & How it Works */}
        <section className="py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <p className="text-lg text-muted-foreground mb-12">{damageType.description}</p>
            
            <h2 className="text-3xl font-bold mb-6">{damageType.howItWorks.title}</h2>
            <p className="text-muted-foreground mb-6">{damageType.howItWorks.description}</p>
            <div className="space-y-4">
              {damageType.howItWorks.steps.map((step, i) => (
                <div key={i} className="flex gap-4 p-4 bg-card rounded-lg border border-border">
                  <div className="flex-shrink-0 w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center text-accent font-bold">{i + 1}</div>
                  <p className="text-sm">
                    <strong className="font-bold">{step.title}:</strong> {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Real Cases */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">Echte Schadensbeispiele</h2>
            <div className="grid lg:grid-cols-2 gap-8">
              {damageType.realCases.map((case_, i) => (
                <div key={i} className="p-8 bg-card rounded-lg border border-border">
                  <h3 className="text-xl font-bold mb-2">{case_.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{case_.location}</p>
                  <p className="text-2xl font-bold text-red-500 mb-4">{case_.damage}</p>
                  <p className="mb-6">{case_.description}</p>
                  <ul className="space-y-2">
                    {case_.consequences.map((cons, j) => (
                      <li key={j} className="flex gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /><span>{cons}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Insurance Protection */}
        <section className="py-16">
          <div className="container mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold mb-12 text-center bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">{damageType.insuranceProtection.title}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {damageType.insuranceProtection.coverages.map((cov, i) => (
                <div key={i} className="p-6 bg-card rounded-lg border-2 border-border hover:border-accent/50 transition-all">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" />{cov.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{cov.description}</p>
                  <div className="p-3 bg-accent/10 rounded text-sm"><strong>Beispiel:</strong> {cov.example}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Best Tariffs */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Top-Tarife für {damageType.name}</h2>
            <div className="space-y-4">
              {damageType.bestTariffs.map((tariff, i) => (
                <div key={i} className="p-6 bg-card rounded-lg border border-border flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-lg">{tariff.tariff}</h3>
                    <p className="text-sm text-muted-foreground">{tariff.provider} • {tariff.coverage}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-accent">{tariff.highlight}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prevention */}
        <section className="py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold mb-8">{damageType.prevention.title}</h2>
            {damageType.prevention.measures.map((measure, i) => (
              <div key={i} className="mb-8">
                <h3 className="text-xl font-bold mb-4">{measure.category}</h3>
                <ul className="space-y-2">
                  {measure.actions.map((action, j) => (
                    <li key={j} className="flex gap-3"><CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" /><span>{action}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Häufige Fragen</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {damageType.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline"><span className="font-semibold">{faq.question}</span></AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
    </>
  );
};

export default DamageTypePage;
